import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useMinuteTick } from '../../hooks/useMinuteTick';
import { cancelPendingRoleMessages } from '../../utils/cancelPendingRoleMessages';
import { formatEffectiveAt, isPending } from '../../utils/gracePeriodFormat';
import { GracePeriodNotice } from '../GracePeriodNotice';

const messages = defineMessages({
  title: {
    id: 'Team.pendingRoleBanner.title',
    defaultMessage: 'Newly granted {roleName} access starts {startsAt}',
    description:
      'Banner title in the edit member tray for a role granted to an existing member that is still waiting out its delay. {roleName} is the role name, e.g. "Admin". {startsAt} is the start date and time, e.g. "Sep 27, 10:00 AM PDT".',
  },
  body: {
    id: 'Team.pendingRoleBanner.body',
    defaultMessage: 'Viewer access applies until then. All admins will be notified.',
    description:
      'Banner body in the edit member tray for a newly granted role that has not started yet. The member has view-only access until the new role starts, and every workspace admin gets an email about the change.',
  },
});

function getEarliestPending(effectiveAts: Date[], now: Date) {
  return effectiveAts
    .filter((effectiveAt) => isPending(effectiveAt, now))
    .reduce<Date | undefined>(
      (earliest, effectiveAt) =>
        earliest == null || effectiveAt < earliest ? effectiveAt : earliest,
      undefined,
    );
}

export type PendingRoleBannerProps = {
  /** Display name of the pending role, e.g. "Admin". */
  roleName: string;
  /**
   * When each pending grant becomes usable, one per legal entity. The banner
   * shows the earliest one that is still pending. Past entries are ignored.
   */
  effectiveAts: Date[];
  /** Whether the viewer may cancel the pending role change. Hides the action when false. */
  canCancel: boolean;
  onCancel: () => void;
  isCancelling?: boolean;
  /** Current time. Defaults to now; pass a fixed value in tests and stories. */
  now?: Date;
  testID?: string;
};

/**
 * Notice for a role that has been granted but is still in its grace period.
 * Rendered once under all of the entities' role rows in the Edit member tray,
 * even when the role is pending on several legal entities. Renders nothing
 * once every grant is in effect.
 */
export function PendingRoleBanner({
  roleName,
  effectiveAts,
  canCancel,
  onCancel,
  isCancelling = false,
  now,
  testID = 'pending-role-banner',
}: PendingRoleBannerProps) {
  const intl = useIntl();
  // Derived on every render (not memoized) so the pending check stays current
  // when `now` is omitted.
  const currentTime = now ?? new Date();
  const nextEffectiveAt = getEarliestPending(effectiveAts, currentTime);

  useMinuteTick(now == null && nextEffectiveAt != null);

  const action = useMemo(
    () =>
      canCancel
        ? {
            label: intl.formatMessage(cancelPendingRoleMessages.revokeAdminAccess),
            onPress: onCancel,
            loading: isCancelling,
          }
        : undefined,
    [canCancel, intl, onCancel, isCancelling],
  );

  if (nextEffectiveAt == null) {
    return null;
  }

  return (
    <GracePeriodNotice
      title={intl.formatMessage(messages.title, {
        roleName,
        startsAt: formatEffectiveAt(intl, nextEffectiveAt),
      })}
      body={intl.formatMessage(messages.body)}
      action={action}
      testID={testID}
    />
  );
}
